import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { usePermissions } from '@shared/hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBankAccountsInfinite } from '../../hooks/useBankAccountsInfinite';
import { useBankAccountsColumns } from '../../hooks/useBankAccountsColumns';
import type { BankAccountsAdvancedFilters, BankAccountsFilters } from '../../models/BankAccount';
import { BankAccount } from '../../models/BankAccount';
import { BankAccountsAgGridTable, BankAccountsBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { CreateBankAccountButton, FiltersSection, StatusToolbar } from '../interaction';
import {
  CreateBankAccountModal,
  CreateBankAccountModalRef,
  DisableBankAccountModal,
  DisableBankAccountModalRef,
  RejectBankAccountModal,
  RejectBankAccountModalRef,
  VerifyBankAccountModal,
  VerifyBankAccountModalRef,
  ViewVerifiedBankAccountModal,
  ViewVerifiedBankAccountModalRef,
} from '../modals';

const getColumnId = (columnId?: string, accessorKey?: string | number) => {
  if (columnId) return columnId;
  if (typeof accessorKey === 'string') return accessorKey;
  return String(accessorKey ?? '');
};

const matchesSearch = (account: BankAccount, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();

  const searchableValues = [
    account.id?.toString(),
    account.holder_name,
    account.iban,
    account.bic,
    account.bank_name,
    account.country_code,
  ];

  return searchableValues.some((value) => value?.toString().toLowerCase().includes(normalized));
};

const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in']);
const VOID_OPERATORS = new Set(['is_null', 'is_not_null']);

const buildAdvancedFiltersPayload = (
  conditions: FilterConditionOutput[],
  statusFilter?: string,
): BankAccountsAdvancedFilters | undefined => {
  const filters = conditions
    .map((condition) => {
      const { column, action } = condition;
      const rawValue = condition.value as unknown;

      if (!column || !action) {
        return null;
      }

      if (VOID_OPERATORS.has(action)) {
        return {
          column,
          operator: action,
        };
      }

      if (MULTI_VALUE_OPERATORS.has(action)) {
        let valuesArray: Array<string | number> = [];

        if (Array.isArray(rawValue)) {
          valuesArray = rawValue as Array<string | number>;
        } else if (typeof rawValue === 'string') {
          valuesArray = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        } else if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          valuesArray = [rawValue as string | number];
        }

        if (!valuesArray.length) {
          return null;
        }

        return {
          column,
          operator: action,
          value: valuesArray,
        };
      }

      const singleValue = Array.isArray(rawValue) ? rawValue[0] : (rawValue as string | number | undefined);

      if (singleValue === undefined || singleValue === null || singleValue === '') {
        return null;
      }

      return {
        column,
        operator: action,
        value: singleValue,
      };
    })
    .filter((filter): filter is BankAccountsAdvancedFilters['filters'][number] => filter !== null);

  // Añadir filtro de status si está seleccionado
  if (statusFilter) {
    filters.push({
      column: 'status',
      operator: 'in',
      value: [statusFilter],
    });
  }

  if (!filters.length) {
    return undefined;
  }

  return {
    filters,
    logic: 'AND',
  };
};

export const Main = () => {
  const { t } = useTranslation('bankAccountsModule');
  const { canAccess } = usePermissions();
  const canCreate = canAccess(['tenant_external'], ['bank-accounts.create']);
  const is3pl = canAccess(['tenant_3pl'], []);

  // Modal refs
  const createModalRef = useRef<CreateBankAccountModalRef>(null);
  const verifyModalRef = useRef<VerifyBankAccountModalRef>(null);
  const rejectModalRef = useRef<RejectBankAccountModalRef>(null);
  const disableModalRef = useRef<DisableBankAccountModalRef>(null);
  const viewDetailsModalRef = useRef<ViewVerifiedBankAccountModalRef>(null);

  const [searchValue, setSearchValue] = useState('');
  const [filterConditions, setFilterConditions] = useState<FilterConditionOutput[]>([]);
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(undefined);

  const advancedFilters = useMemo(
    () => buildAdvancedFiltersPayload(filterConditions, selectedStatus),
    [filterConditions, selectedStatus],
  );

  const bankAccountsFilters = useMemo<BankAccountsFilters>(() => {
    const next: BankAccountsFilters = {};
    if (advancedFilters) next.advanced_filters = advancedFilters;
    if (teamIds.length > 0) next.team_ids = teamIds.join(',');
    return next;
  }, [advancedFilters, teamIds]);

  const { data, isPending, isError, error, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useBankAccountsInfinite(bankAccountsFilters);

  // Modal handlers
  const handleOpenCreateModal = useCallback(() => {
    createModalRef.current?.open();
  }, []);

  const handleVerify = useCallback((account: BankAccount) => {
    verifyModalRef.current?.open(account);
  }, []);

  const handleReject = useCallback((account: BankAccount) => {
    rejectModalRef.current?.open(account);
  }, []);

  const handleDisable = useCallback((account: BankAccount) => {
    disableModalRef.current?.open(account);
  }, []);

  const handleViewDocument = useCallback((account: BankAccount) => {
    if (account.proof_document_path) {
      window.open(account.proof_document_path, '_blank');
    }
  }, []);

  const handleViewDetails = useCallback((account: BankAccount) => {
    viewDetailsModalRef.current?.open(account);
  }, []);

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const { tanStackColumns, filteredColumnDefs } = useBankAccountsColumns({
    onVerify: is3pl ? handleVerify : undefined,
    onReject: is3pl ? handleReject : undefined,
    onDisable: handleDisable,
    onViewDocument: handleViewDocument,
    onViewDetails: handleViewDetails,
    selectedColumns,
  });

  const columnIds = useMemo(
    () =>
      tanStackColumns.map((column) =>
        getColumnId(
          column.id as string,
          Object.prototype.hasOwnProperty.call(column, 'accessorKey')
            ? (column as { accessorKey?: string | number }).accessorKey
            : undefined,
        ),
      ),
    [tanStackColumns],
  );

  useEffect(() => {
    setSelectedColumns((current) => {
      if (current.length === columnIds.length && current.every((id) => columnIds.includes(id))) {
        return current;
      }
      return columnIds;
    });
  }, [columnIds]);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleFilterChange = useCallback((conditions: FilterConditionOutput[]) => {
    setFilterConditions(conditions);
  }, []);

  const handleColumnsChange = useCallback((columnsIds: string[]) => {
    setSelectedColumns(columnsIds);
  }, []);

  const handleStatusChange = useCallback((status?: string) => {
    setSelectedStatus(status);
  }, []);

  const bankAccounts = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const totalFromApi = data?.pages[0]?.meta.total;

  const filteredBankAccounts = useMemo(
    () => bankAccounts.filter((account) => matchesSearch(account, searchValue)),
    [bankAccounts, searchValue],
  );

  const hasActiveFilters = Boolean(searchValue?.trim()) || filterConditions.length > 0 || teamIds.length > 0;
  const isRefetching = isFetching && !isPending && !isFetchingNextPage;

  if (isPending) {
    return <LoadingState message={t('loading.accounts')} />;
  }

  if (isError || !data) {
    return <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} title={t('errors.title')} />;
  }

  return (
    <>
      <BankAccountsBreadcrumb />
      <div className="px-6 pt-4">
        {/* Botón principal en la barra superior (alineado a la derecha) */}
        <div className="shrink-0 absolute right-4 top-2 flex items-center gap-2">
          {canCreate && <CreateBankAccountButton onClick={handleOpenCreateModal} />}
        </div>

        {/* Header con filtros y botón crear */}
        <div className="mb-4">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <FiltersSection
              columns={tanStackColumns as ColumnDef<BankAccount>[]}
              selectedColumns={selectedColumns}
              fixedColumns={['actions']}
              onColumnsChange={handleColumnsChange}
              searchValue={searchValue}
              onSearch={handleSearch}
              filterConditions={filterConditions}
              onFilterChange={handleFilterChange}
              teamIds={teamIds}
              onTeamIdsChange={setTeamIds}
            />

            <div className="flex flex-col items-end ml-auto">
              <TotalResults
                total={
                  searchValue?.trim() ? filteredBankAccounts.length : (totalFromApi ?? filteredBankAccounts.length)
                }
              />
            </div>
          </div>
          {/* Tabs de status */}
          {!isPending && (
            <StatusToolbar onStatusChange={handleStatusChange} active={activeTab} setActive={setActiveTab} />
          )}
        </div>

        {/* Tabla */}
        {filteredBankAccounts.length === 0 ? (
          <EmptyView
            type={hasActiveFilters ? 'noResults' : 'emptyState'}
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={hasActiveFilters ? t('noResults') : t('emptyState')}
          />
        ) : (
          <div
            className={`transition-opacity duration-300 ${
              isRefetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
            }`}
          >
            <BankAccountsAgGridTable
              bankAccounts={filteredBankAccounts}
              columnDefs={filteredColumnDefs}
              fetchNextPage={() => {
                void fetchNextPage();
              }}
              hasNextPage={hasNextPage ?? false}
              isFetchingNextPage={isFetchingNextPage}
            />
          </div>
        )}
      </div>

      {/* Modales */}
      <CreateBankAccountModal ref={createModalRef} onSuccess={refetch} />
      <VerifyBankAccountModal ref={verifyModalRef} onSuccess={refetch} />
      <RejectBankAccountModal ref={rejectModalRef} onSuccess={refetch} />
      <DisableBankAccountModal ref={disableModalRef} onSuccess={refetch} />
      <ViewVerifiedBankAccountModal ref={viewDetailsModalRef} showStaffFields={is3pl} />
    </>
  );
};
